import {
  AUTH_HEADER,
  AUTH_TYPES,
  BASIC_AUTH_PREFIX,
  FORM_URL_ENCODED_CONTENT_TYPE,
} from './constants';
import type {
  JsonBody,
  KeyValuePair,
  UserFieldOption,
  WebhookUserJsonField,
  WorkflowWebhook,
  WorkflowWebhookBody,
} from './types';

const isBasicAuthorizationHeader = (header: KeyValuePair) => {
  return header.key === AUTH_HEADER && header.value.indexOf(BASIC_AUTH_PREFIX) === 0;
};

const isUserJsonField = (body: WorkflowWebhookBody): body is WebhookUserJsonField => {
  return (body as WebhookUserJsonField).field !== undefined;
};

const isKeyValuePair = (body: WorkflowWebhookBody): body is KeyValuePair => {
  return (body as KeyValuePair).key !== undefined;
};

const isJsonBody = (body: WorkflowWebhookBody): body is JsonBody => {
  return (body as JsonBody).jsBody !== undefined;
};

export const transformUserFieldMappings = (userJsonFields: WebhookUserJsonField[]) => {
  return Object.values(userJsonFields)
    .sort((a, b) => a.field.localeCompare(b.field))
    .map((f, i): UserFieldOption => ({ id: i.toString(), label: f.field, value: f.field }));
};

export const transformWorkflowWebhook = (workflowWebhook: WorkflowWebhook): WorkflowWebhook => {
  // If auth type is basic, omit the Authorization header, transforming the value of that header into the basicAuthToken field
  // { headers: [{ key: "Authorization", value: "123" }] } -> { basicAuthToken: "123" }
  const isBasicAuth = workflowWebhook.authType === AUTH_TYPES.Basic;
  const basicAuthHeader = workflowWebhook.headers?.find(isBasicAuthorizationHeader) || null;
  const basicAuthToken = basicAuthHeader?.value?.replace(BASIC_AUTH_PREFIX, '') || '';

  const headersWithoutAuth =
    workflowWebhook.headers?.filter((h) => !isBasicAuthorizationHeader(h)) || [];
  const headers = isBasicAuth ? headersWithoutAuth : workflowWebhook.headers || [];

  const body = workflowWebhook.body ? workflowWebhook.body : [];
  const userFields = body.filter(isUserJsonField);
  const jsonBody = body.find(isJsonBody);
  const jsonBodyText = jsonBody ? JSON.stringify(jsonBody.jsBody, null, 4) : '';

  const formBody =
    workflowWebhook.contentType === FORM_URL_ENCODED_CONTENT_TYPE
      ? body.filter(isKeyValuePair)
      : [];

  return {
    ...workflowWebhook,
    headers,
    basicAuthToken,
    destination: 'Custom',
    userFields,
    formBody,
    jsBody: jsonBodyText,
  };
};

export const transformWorkflowWebhookInput = (workflowWebhook: WorkflowWebhook) => {
  const {
    basicAuthToken,
    clientSecret,
    modifiedClientSecret,
    contentType,
    formBody,
    userFields,
    headers,
    jsBody,
  } = workflowWebhook;

  // If auth type is basic, omit the Authorization header, transform the value of basicAuthToken into a new Authorization header
  // { basicAuthToken: "123" } -> { headers: [{ key: "Authorization", value: "123" }] }
  const isBasicAuth = workflowWebhook.authType === AUTH_TYPES.Basic;
  const basicAuthHeader =
    isBasicAuth && basicAuthToken
      ? [{ key: AUTH_HEADER, value: `${BASIC_AUTH_PREFIX}${basicAuthToken}` }]
      : [];
  const mergedHeaders = [...headers, ...basicAuthHeader];

  const mergedJsBody = jsBody ? [{ jsBody: JSON.parse(jsBody) }] : [];
  const mergedBody = [
    // ...(includeDefaultFields ? [{ field: "workflowId" }] : []),
    ...userFields.filter((f) => !f.deleted),
    ...(contentType === FORM_URL_ENCODED_CONTENT_TYPE ? formBody : mergedJsBody),
  ];

  return {
    ...workflowWebhook,
    headers: mergedHeaders.length > 0 ? mergedHeaders : null,
    clientSecret: modifiedClientSecret ? clientSecret : null,
    bodyOpt: mergedBody.length > 0 ? mergedBody : null,
  };
};
